import i18n, { t } from '@if/core/i18n';
import { EGender, EHomeStatus, EJobType, ELanguage, EOtherIncomeType, getEGenderList, getELanguageList, } from '@if/core/types';
import * as yup from '@if/core/yup';
import { EJobTypeWithUnemployed, isNonEmployedJobType } from '../types/constants';
const civicNumberRegex = /^([0-9]+)([a-zA-Z])?$/;
export const nameRegex = /^([-A-Z\u00C0-\u00DC]|[a-z\u00E0-\u00FC])+([-A-Z\u00C0-\u00DC]|[a-z\u00E0-\u00FC]|['.\s])*$/i;
const sinRegex = /^[0-9]*$/;
export const BaseApplicantSchema = () => yup.object({
    genderId: yup.numberEnum(getEGenderList()).required().default(EGender.Men),
    languageId: yup
        .numberEnum(getELanguageList())
        .required()
        .default(() => (i18n.language == 'fr' ? ELanguage.French : ELanguage.English)),
    birthDate: yup
        .string()
        .validDate()
        .validAgeRequirement(yup.ref('currentAddress.stateIso'), () => t('common.errors.minimumAgeRequired', { ns: 'shared' }))
        .required(),
    firstName: yup.string().required().matches(nameRegex, 'common.errors.invalidCharacters').max(50),
    middleName: yup.string().emptyToNull().nullable().matches(nameRegex, 'common.errors.invalidCharacters').max(50),
    lastName: yup.string().required().matches(nameRegex, 'common.errors.invalidCharacters').max(50),
    sin: yup.string().matches(sinRegex, 'common.errors.invalidCharacters').validSIN().default('').defined(),
    cellPhone: yup.string().validCanadianPhone().required(),
    homePhone: yup.string().validCanadianPhone().nullable(),
    email: yup.string().required().email().max(60).default(''),
    currentJobs: yup.array(JobSchema).min(1).default([]),
    otherIncomes: yup.array(OtherIncomeSchema).default([OtherIncomeSchema.getDefault()]),
    currentAddress: createAddressSchema().default(AddressSchema.getDefault()),
    spending: ExpensesSchema.default(ExpensesSchema.getDefault()),
    subscribeNewsletter: yup.boolean().default(false),
    hasLegalName: yup.boolean().default(false),
    legalName: yup.string().emptyToNull().nullable().matches(nameRegex).max(50),
});
export const createAddressSchema = (birthDateValue) => yup.object({
    civicNumber: yup.string().emptyToNull().matches(civicNumberRegex).required().max(10).default(''),
    street: yup.string().emptyToNull().required().max(100).default(''),
    apartment: yup.string().max(50),
    suite: yup.string().max(10),
    postalCode: yup.string().required().validCanadianPostalCode().default(''),
    city: yup.string().emptyToNull().required().max(50).default(''),
    stateIso: yup
        .string()
        .required()
        .default('')
        .validProvinceRequirement(birthDateValue, () => t('common.errors.minimumAgeRequired', { ns: 'shared' })),
    country: yup.string().default('Canada').oneOf(['Canada'], 'The country must be Canada'),
    years: yup.integer().nullable().default(0),
    monthsUnderOneYear: yup.integer().nullable().default(0),
    months: yup.integer().positive().default(0).required(),
});
export const AddressSchema = createAddressSchema();
export const ExpensesSchema = yup.object({
    homeFeeTypeId: yup.mixed().required().default(EHomeStatus.Renter),
    totalMonthlyHomePayment: yup
        .integer()
        .min(0)
        .default(0)
        .when('homeFeeTypeId', {
        is: (val) => {
            return val !== EHomeStatus.Owner && val !== EHomeStatus.WithRelative;
        },
        then: (schema) => schema.positive().required(),
        otherwise: (schema) => schema.nullable(),
    }),
    rentMonthly: yup.integer(),
    mortgageMonthly: yup.integer(),
    housingMonthly: yup
        .integer()
        .positive()
        .min(0)
        .nullable()
        .default(null)
        .when('totalMonthlyHomePayment', {
        is: (val) => {
            return val > 0;
        },
        then: (schema) => schema.required().max(yup.ref('totalMonthlyHomePayment')),
    }),
});
export const JobSchema = yup.object({
    jobType: yup.numberEnum().required().default(EJobType.FullTime),
    employerName: yup
        .string()
        .max(100)
        .when('jobType', {
        is: isNonEmployedJobType,
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema.emptyToNull().required().default(''),
    }),
    jobTitle: yup.string().when('jobType', {
        is: isNonEmployedJobType,
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema.emptyToNull().required().max(50).default(''),
    }),
    annualSalary: yup.integer().when('jobType', {
        is: isNonEmployedJobType,
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema
            .typeError(() => t('common.errors.integerValueRequired', { ns: 'shared' }))
            .required()
            .min(1)
            .default(null),
    }),
    employerPhone: yup.string().when('jobType', {
        is: isNonEmployedJobType,
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema.validCanadianPhone().required().default(''),
    }),
    employerPhoneExt: yup.string().validPhoneExtension().nullable(),
    months: yup.integer().when('jobType', {
        is: isNonEmployedJobType,
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema
            .positive(() => t('common.errors.required', { ns: 'shared' }))
            .required()
            .default(1),
    }),
    monthsUnderOneYear: yup.integer().nullable(),
    years: yup.integer().nullable(),
});
export const OtherIncomeSchema = yup.object({
    jobType: yup.integer().default(EJobTypeWithUnemployed.Unemployed).required(),
    typeId: yup.integer().when('jobType', {
        is: isNonEmployedJobType,
        then: (schema) => schema.required(),
    }),
    annualAmount: yup
        .integer()
        .typeError('common.errors.integerValueRequired')
        .when('typeId', {
        is: (value) => value !== null,
        then: (schema) => schema.required().min(1),
    }),
    description: yup
        .string()
        .nullable()
        .when('typeId', {
        is: (val) => val === EOtherIncomeType.Other,
        then: (schema) => schema.required(),
    }),
});
export const ContactInformationInputSchema = BaseApplicantSchema()
    .pick(['cellPhone', 'homePhone', 'email', 'subscribeNewsletter'])
    .shape({
    applicantPhoneNumber: yup.string().validCanadianPhone().optional(),
    cellPhone: yup
        .string()
        .validCanadianPhone()
        .required()
        .test('is-different-from-given-number', () => t('contact.coappSameMobileError', { ns: 'shared' }), (value, ctx) => {
        const applicantPhoneNumber = ctx.parent?.applicantPhoneNumber;
        return !applicantPhoneNumber || value !== applicantPhoneNumber;
    }),
});
export const HomeInformationInputSchema = (birthDate) => {
    return BaseApplicantSchema()
        .shape({
        currentAddress: createAddressSchema(birthDate),
    })
        .pick(['currentAddress', 'spending']);
};
export const EmploymentInputSchema = BaseApplicantSchema().pick(['currentJobs', 'otherIncomes']);
